// Content for the public /docs page: how the GrabNChat portal works + the
// public REST API (/api/v1) reference. Source of truth for the endpoints is
// the backend (whatsapp-nodejs/backend/server.js, the `v1` router) — keep
// this file in sync when an endpoint's request/response shape changes.

import { BRAND } from "@/data/site";

// Public API host. Override per environment with REACT_APP_PUBLIC_API_URL
// (e.g. https://test.grabnchat.com on staging).
export const API_HOST = (process.env.REACT_APP_PUBLIC_API_URL || "https://backend.grabnchat.com").replace(/\/+$/, "");
export const API_BASE = `${API_HOST}/api/v1`;
export const PORTAL_URL = BRAND.appUrl;

const LIVE_KEY = "gnc_live_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx";
const PUB_KEY = "gnc_pub_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx";
export const EXAMPLE_KEYS = { live: LIVE_KEY, pub: PUB_KEY };

// ---------------------------------------------------------------------------
// Portal guide
// ---------------------------------------------------------------------------

export const GETTING_STARTED = [
  { title: "Create your account", text: `Sign up at ${PORTAL_URL.replace(/^https?:\/\//, "")}. Every account is a company workspace — the person who signs up is the company admin and can invite agents later.` },
  { title: "Connect WhatsApp", text: "Open Manage → Channels and connect your WhatsApp Business number through Meta's Embedded Signup. Facebook Pages and Instagram accounts are connected from the same screen." },
  { title: "Top up the message wallet", text: "WhatsApp messages are charged per conversation category (marketing, utility, authentication, service) from your prepaid message wallet. Top it up from Manage → Billing." },
  { title: "Create message templates", text: "Build templates in Engage → Template Message and submit them to Meta. Only APPROVED templates can be sent to customers outside the 24-hour customer-service window." },
  { title: "Bring in your contacts", text: "Add or import contacts in Contacts, organise them with Tags and custom Attributes, or let leads flow in automatically from your website, Google Sheets, Facebook Lead Ads and more." },
  { title: "Engage, automate & reply", text: "Send broadcasts and drip campaigns, build chatbots and WhatsApp Flows, and answer every conversation from the shared Team Inbox." },
];

export const PORTAL_MODULES = [
  {
    group: "Workspace",
    items: [
      { name: "Dashboard", text: "Live overview of messages sent / received, delivery & read rates, conversations and contacts." },
      { name: "Inbox", text: "Shared team inbox for WhatsApp, Instagram, Messenger and website chat. Assign conversations to agents, reply in real time and see full history." },
      { name: "Contacts", text: "Your customer database. Contacts are de-duplicated by phone number and enriched with tags and custom attributes." },
      { name: "Tags & Attributes", text: "Segment contacts with tags (e.g. vip, shopify-customer) and store custom fields you can use as template variables." },
    ],
  },
  {
    group: "Engage",
    items: [
      { name: "Broadcast", text: "Send an approved template to a list, tag or segment of contacts and track sent, delivered, read and failed counts." },
      { name: "Drip Campaigns", text: "Multi-step sequences that send templates on a schedule after a contact enrols." },
      { name: "Click to WhatsApp", text: "Links and QR codes that open a WhatsApp chat with your business, with a pre-filled message." },
      { name: "Flow builder", text: "Visual, no-code conversation flows with buttons, lists, conditions and hand-off to a human agent." },
      { name: "Template Message", text: "Create, preview and submit WhatsApp templates (text, image, video or document headers, buttons) for Meta approval." },
      { name: "WhatsApp Flows", text: "Native in-chat forms (lead capture, booking, surveys) built on Meta's WhatsApp Flows." },
    ],
  },
  {
    group: "Automate",
    items: [
      { name: "Lead Management", text: "A pipeline for leads from your website, Google Sheets, Facebook / Instagram Lead Ads and the API — with stages, owners and follow-ups." },
      { name: "Chatbot", text: "AI and rule-based bots for WhatsApp and your website chat widget." },
      { name: "Keywords", text: "Auto-reply or start a flow when an incoming message matches a keyword." },
      { name: "Integrations", text: "Shopify, Google Sheets, Facebook Lead Ads, Google Business Profile and more." },
    ],
  },
  {
    group: "Channels & growth",
    items: [
      { name: "WhatsApp Calling", text: "Business-initiated and user-initiated WhatsApp voice calls with call logs and webhooks." },
      { name: "Facebook & Instagram", text: "Manage Pages, posts, insights, reviews, comments, Messenger broadcasts and Instagram media from one place." },
      { name: "Google Business Profile", text: "Profile, posts, media, reviews and a review-collection rating link." },
      { name: "AI Content", text: "Generate social posts and short videos with AI." },
    ],
  },
  {
    group: "Manage",
    items: [
      { name: "E-commerce & Catalogs", text: "Sync your product catalog and share products inside WhatsApp chats." },
      { name: "Agents & Team", text: "Invite agents, set roles and control which conversations each agent sees." },
      { name: "API Integration", text: "Create API keys, browse the endpoint catalog and copy ready-made lead-capture snippets." },
      { name: "Billing", text: "Subscription plan, invoices, payment history and the WhatsApp message wallet." },
    ],
  },
];

export const MESSAGE_LIFECYCLE = [
  { step: "Your request", text: "You call the API (or click Send in the portal)." },
  { step: "Wallet check", text: "The estimated message cost is reserved from your message wallet. If the balance is too low the request stops with 402." },
  { step: "Sent to Meta", text: "The message is sent through the official WhatsApp Cloud API from your connected number." },
  { step: "Saved to Inbox", text: "The contact and conversation are created or updated and the message appears live in the Team Inbox." },
  { step: "Status updates", text: "Meta reports sent → delivered → read (or failed). The final charge is settled and a latest_status realtime event is emitted." },
];

// ---------------------------------------------------------------------------
// Common responses
// ---------------------------------------------------------------------------

const r400Json = {
  code: 400,
  title: "Bad Request",
  when: "The request body is not valid JSON (e.g. a trailing comma or an unquoted key).",
  body: { detail: "Unexpected token } in JSON at position 42" },
};
const r400NoBody = {
  code: 400,
  title: "Bad Request",
  na: true,
  when: "This endpoint takes no request body, so it does not return 400 in normal operation. Any 400 from the platform uses the same shape:",
  body: { detail: "Error message" },
};
const r401 = {
  code: 401,
  title: "Unauthorized",
  when: "The X-API-Key header is missing, or the key is invalid or has been revoked.",
  body: { detail: "Invalid or revoked API key" },
};
const r403 = {
  code: 403,
  title: "Forbidden",
  when: "The API key is valid but the company account is inactive or suspended.",
  body: { detail: "Company inactive" },
};
const r404Route = (path) => ({
  code: 404,
  title: "Not Found",
  when: "The URL or HTTP method is wrong (typo in the path, missing /api/v1 prefix, GET instead of POST).",
  body: { detail: "Not Found", path },
});
const r422Missing = (field) => ({
  code: 422,
  title: "Unprocessable Entity",
  when: "A required field is missing or empty.",
  body: { detail: `Missing required field: ${field}` },
});
const r500 = {
  code: 500,
  title: "Internal Server Error",
  when: "An unexpected error on our side. Safe to retry with exponential back-off; contact support if it persists.",
  body: { detail: "Internal server error" },
};
const r402 = {
  code: 402,
  title: "Payment Required",
  when: "Your message wallet balance cannot cover this message. Top up from Billing in the portal.",
  body: { detail: "Insufficient message wallet balance — need ₹0.86 for this marketing message, have ₹0.20." },
};
const r400WaNotConnected = {
  code: 400,
  title: "Bad Request",
  when: "No WhatsApp number is connected to this company yet (connect one from Channels), or the body is not valid JSON.",
  body: { detail: "WhatsApp not connected for this company" },
};

const sentMessage = (type, extra = {}) => ({
  message: {
    id: "4f1c2b7e-8a3d-4a51-9a0e-2b7d6c1e9f10",
    company_id: "c0a8012e-5b6f-4c3d-9e21-7f8a9b0c1d2e",
    conversation_id: "9d2e1f30-4b5c-4d6e-8f70-a1b2c3d4e5f6",
    contact_id: "1a2b3c4d-5e6f-4a7b-8c9d-0e1f2a3b4c5d",
    channel: "whatsapp",
    direction: "out",
    type,
    status: "sent",
    wamid: "wamid.HBgMOTE5OTk5OTk5OTk5FQIAERgSRjU3QkE2QzM0RTVCMDg3AA==",
    timestamp: "2026-09-29T10:15:42.118Z",
    ...extra,
  },
  graph_status: 200,
  graph_response: {
    messaging_product: "whatsapp",
    contacts: [{ input: "+919999999999", wa_id: "919999999999" }],
    messages: [{ id: "wamid.HBgMOTE5OTk5OTk5OTk5FQIAERgSRjU3QkE2QzM0RTVCMDg3AA==" }],
  },
});

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export const API_GROUPS = ["Health", "WhatsApp", "Messages", "Templates", "WhatsApp Flows", "Contacts", "Leads", "Analytics", "Realtime"];

export const ENDPOINTS = [
  // ----------------------------------------------------------------- Health
  {
    id: "ping",
    group: "Health",
    name: "Ping",
    method: "GET",
    path: "/ping",
    auth: "live",
    summary: "Checks that your API key works and tells you which company it belongs to. Use it as a health check when setting up an integration.",
    request: null,
    responses: [
      { code: 200, title: "OK", when: "The key is valid.", body: { ok: true, company_id: "c0a8012e-5b6f-4c3d-9e21-7f8a9b0c1d2e", api_key: "Mobile app", server_time: "2026-09-29T10:15:42.118Z" } },
      r400NoBody,
      r401,
      r403,
      r404Route("/api/v1/pingg"),
      r500,
    ],
  },

  // --------------------------------------------------------------- WhatsApp
  {
    id: "whatsapp-status",
    group: "WhatsApp",
    name: "Get WhatsApp connection status",
    method: "GET",
    path: "/whatsapp/status",
    auth: "live",
    summary: "Returns the WhatsApp Business Account and phone number connected to your company. When nothing is connected yet, returns { status: \"not_connected\" } with HTTP 200.",
    request: null,
    responses: [
      { code: 200, title: "OK", when: "Connected number (or status: \"not_connected\").", body: { company_id: "c0a8012e-5b6f-4c3d-9e21-7f8a9b0c1d2e", waba_id: "104857392018475", phone_number_id: "282198141632775", display_phone_number: "+91 93115 72747", verified_name: "GrabNChat", status: "active", quality_rating: "GREEN" } },
      r400NoBody,
      r401,
      r404Route("/api/v1/whatsapp/statuss"),
      r500,
    ],
  },

  // --------------------------------------------------------------- Messages
  {
    id: "send-text",
    group: "Messages",
    name: "Send text message",
    method: "POST",
    path: "/messages/text",
    auth: "live",
    summary: "Sends a plain text WhatsApp message. Free-form text can only be delivered while the 24-hour customer-service window is open (the customer messaged you in the last 24 hours) — otherwise use a template.",
    params: [
      { name: "to", type: "string", required: true, desc: "Recipient phone number in international format, e.g. +919999999999." },
      { name: "text", type: "string", required: true, desc: "Message text (max 4096 characters)." },
    ],
    request: { to: "+919999999999", text: "Hi Rohit, your order #1042 has been shipped!" },
    notes: ["If Meta rejects the message (e.g. the 24-hour window is closed) the API still responds 200, with message.status = \"failed\", message.error set and graph_status holding Meta's HTTP status. Always check message.status."],
    responses: [
      { code: 200, title: "OK", when: "Message accepted and sent to WhatsApp.", body: sentMessage("text", { text: "Hi Rohit, your order #1042 has been shipped!" }) },
      r400WaNotConnected,
      r401,
      r402,
      r404Route("/api/v1/message/text"),
      r422Missing("text"),
      r500,
    ],
  },
  {
    id: "send-template",
    group: "Messages",
    name: "Send template message",
    method: "POST",
    path: "/messages/template",
    auth: "live",
    summary: "Sends an approved template to one recipient using Meta's raw components format. Templates can be sent at any time, including outside the 24-hour window.",
    params: [
      { name: "to", type: "string", required: true, desc: "Recipient phone number in international format." },
      { name: "template_name", type: "string", required: true, desc: "Exact name of an APPROVED template." },
      { name: "language", type: "string", required: false, desc: "Template language code. Default en_US." },
      { name: "components", type: "array", required: false, desc: "Meta components array with header / body / button parameters. Omit if the template has no variables." },
    ],
    request: {
      to: "+919999999999",
      template_name: "order_update",
      language: "en_US",
      components: [{ type: "body", parameters: [{ type: "text", text: "Rohit" }, { type: "text", text: "ORD-1042" }, { type: "text", text: "shipped" }] }],
    },
    notes: ["Prefer /messages/template/bulk if you don't want to build the components array yourself — it accepts simple body_params / header_link values."],
    responses: [
      { code: 200, title: "OK", when: "Template accepted and sent to WhatsApp.", body: sentMessage("template", { template: { name: "order_update", language: { code: "en_US" } } }) },
      r400WaNotConnected,
      r401,
      r402,
      r404Route("/api/v1/messages/templates"),
      r422Missing("template_name"),
      r500,
    ],
  },
  {
    id: "send-template-bulk",
    group: "Messages",
    name: "Send template to one or many recipients",
    method: "POST",
    path: "/messages/template/bulk",
    auth: "live",
    summary: "Sends an approved template, by name, to up to 500 recipients in a single call. Fills header and body variables from simple arrays, so you never have to build Meta's components format.",
    params: [
      { name: "template_name", type: "string", required: true, desc: "Exact name of an APPROVED template." },
      { name: "recipients", type: "string[]", required: false, desc: "List of phone numbers (max 500). Either recipients or to is required." },
      { name: "to", type: "string", required: false, desc: "Single recipient (alternative to recipients)." },
      { name: "language", type: "string", required: false, desc: "Template language code. Default en_US." },
      { name: "body_params", type: "string[]", required: false, desc: "Values for {{1}}, {{2}}… in the BODY, in order. Count must match the template." },
      { name: "header_params", type: "string[]", required: false, desc: "One value for a TEXT header containing {{1}}." },
      { name: "header_link", type: "string", required: false, desc: "Public URL of the file for an IMAGE / VIDEO / DOCUMENT header." },
      { name: "header_filename", type: "string", required: false, desc: "File name shown for a DOCUMENT header." },
      { name: "components", type: "array", required: false, desc: "Raw Meta components (overrides all of the above)." },
    ],
    request: {
      template_name: "daily_report",
      language: "en_US",
      recipients: ["+919999999999", "+918888888888"],
      header_link: "https://example.com/reports/2026-09-29.pdf",
      header_filename: "Daily report.pdf",
      body_params: ["Rohit", "29 Sep 2026"],
    },
    notes: ["Each recipient is sent independently: one failure does not stop the rest. Check failed and errors in the response."],
    responses: [
      { code: 200, title: "OK", when: "Batch processed. Some recipients may have failed — see errors.", body: { total: 2, sent: 1, failed: 1, errors: [{ to: "+918888888888", error: "Insufficient message wallet balance — need ₹0.86 for this marketing message, have ₹0.20." }] } },
      { code: 400, title: "Bad Request", when: "More than 500 recipients in one call (or the body is not valid JSON).", body: { detail: "This send would reach 750 recipients — the cap is 500 per call. Split into smaller batches." } },
      r401,
      { code: 404, title: "Not Found", when: "No template with this name exists in your account (a wrong URL returns { detail: \"Not Found\", path }).", body: { detail: "Template \"daily_reportt\" not found" } },
      { code: 422, title: "Unprocessable Entity", when: "No recipients, template not APPROVED, or the number of body_params / header values doesn't match the template.", body: { detail: "Template \"daily_report\" body needs 2 parameter(s) (found {{1}}..{{2}}), but 1 were provided." } },
      r500,
    ],
  },
  {
    id: "reply",
    group: "Messages",
    name: "Reply to a conversation",
    method: "POST",
    path: "/messages/reply",
    auth: "live",
    summary: "Replies with text inside an existing conversation. Pass conversation_id (from realtime events) and the platform picks the right channel — WhatsApp or website chat — automatically.",
    params: [
      { name: "text", type: "string", required: true, desc: "Reply text." },
      { name: "conversation_id", type: "string", required: false, desc: "Conversation to reply in. Either conversation_id or to is required." },
      { name: "to", type: "string", required: false, desc: "Recipient phone number (when you don't have a conversation_id)." },
    ],
    request: { conversation_id: "9d2e1f30-4b5c-4d6e-8f70-a1b2c3d4e5f6", text: "Hi! How can we help you today?" },
    responses: [
      { code: 200, title: "OK", when: "Reply sent.", body: sentMessage("text", { text: "Hi! How can we help you today?" }) },
      r400WaNotConnected,
      r401,
      r402,
      { code: 404, title: "Not Found", when: "conversation_id does not exist in your account.", body: { detail: "Conversation not found" } },
      { code: 422, title: "Unprocessable Entity", when: "text is missing, or neither conversation_id nor to was sent.", body: { detail: "Either conversation_id or to is required" } },
      r500,
    ],
  },
  {
    id: "template-log",
    group: "Messages",
    name: "Log an externally sent template",
    method: "POST",
    path: "/messages/template-log",
    auth: "none",
    summary: "For systems that send templates directly through Meta's Graph API with their own credentials. This endpoint does not send anything — it records the send so it appears in your GrabNChat Inbox, creates the contact and (optionally) tags it. Call it right after your own send, whether it succeeded or failed.",
    params: [
      { name: "phone_number_id", type: "string", required: true, desc: "Meta phone number ID you sent FROM (identifies your company)." },
      { name: "to", type: "string", required: true, desc: "Recipient phone number." },
      { name: "template_name", type: "string", required: true, desc: "Template that was sent." },
      { name: "language", type: "string", required: false, desc: "Default en_US." },
      { name: "components", type: "array", required: false, desc: "The components array you sent to Meta." },
      { name: "wamid", type: "string", required: false, desc: "Meta message ID. Retries with the same wamid are de-duplicated." },
      { name: "status", type: "\"sent\" | \"failed\"", required: false, desc: "Default sent." },
      { name: "error", type: "object", required: false, desc: "Meta's error object when status is failed." },
      { name: "contact_name", type: "string", required: false, desc: "Name for a new contact." },
      { name: "tag", type: "string", required: false, desc: "Tag to add to the contact (created if it doesn't exist)." },
    ],
    request: { phone_number_id: "282198141632775", to: "+919999999999", template_name: "order_update", language: "en_US", wamid: "wamid.HBgMOTE5OTk5OTk5OTk5FQIAERgSRjU3QkE2QzM0RTVCMDg3AA==", status: "sent", contact_name: "Rohit Sharma", tag: "dealer" },
    responses: [
      { code: 200, title: "OK", when: "Send recorded. duplicate: true is returned when this wamid was already logged.", body: { ok: true, message: { id: "4f1c2b7e-8a3d-4a51-9a0e-2b7d6c1e9f10", conversation_id: "9d2e1f30-4b5c-4d6e-8f70-a1b2c3d4e5f6", contact_id: "1a2b3c4d-5e6f-4a7b-8c9d-0e1f2a3b4c5d", wamid: "wamid.HBgMOTE5OTk5OTk5OTk5FQIAERgSRjU3QkE2QzM0RTVCMDg3AA==", direction: "out", channel: "whatsapp", type: "template", template: { name: "order_update", language: { code: "en_US" } }, status: "sent", source: "external_api", timestamp: "2026-09-29T10:15:42.118Z" } } },
      r400Json,
      { code: 404, title: "Not Found", when: "phone_number_id doesn't match any connected WhatsApp number.", body: { detail: "No company found for this phone_number_id" } },
      r422Missing("phone_number_id"),
      r500,
    ],
  },

  // -------------------------------------------------------------- Templates
  {
    id: "list-templates",
    group: "Templates",
    name: "List templates",
    method: "GET",
    path: "/templates",
    auth: "live",
    summary: "Lists every message template in your account (DRAFT, PENDING, APPROVED, REJECTED), newest first.",
    params: [{ name: "limit", in: "query", type: "number", required: false, desc: "Maximum results. Default 200, max 500." }],
    query: "?limit=200",
    request: null,
    responses: [
      { code: 200, title: "OK", when: "List returned (data is empty if you have no templates).", body: { data: [{ id: "7b3e…", name: "order_update", language: "en_US", category: "UTILITY", status: "APPROVED", components: [{ type: "BODY", text: "Hi {{1}}, your order {{2}} is now {{3}}." }], created_at: "2026-09-01T08:00:00.000Z" }], count: 1 } },
      r400NoBody,
      r401,
      r404Route("/api/v1/template"),
      r500,
    ],
  },
  {
    id: "get-template",
    group: "Templates",
    name: "Get template by name",
    method: "GET",
    path: "/templates/{name}",
    examplePath: "/templates/order_update",
    auth: "live",
    summary: "Fetches one template by its exact name. Besides Meta's raw components it returns ready-to-use fields — body_text, header_text, footer_text, sample values and the number of variables — so you don't have to parse components.",
    params: [{ name: "name", in: "path", type: "string", required: true, desc: "Exact template name." }],
    request: null,
    responses: [
      { code: 200, title: "OK", when: "Template found.", body: { data: { id: "7b3e…", name: "order_update", language: "en_US", category: "UTILITY", status: "APPROVED", components: [{ type: "BODY", text: "Hi {{1}}, your order {{2}} is now {{3}}." }], header_format: "NONE", header_text: "", body_text: "Hi {{1}}, your order {{2}} is now {{3}}.", footer_text: "", header_example: [], body_example: ["Rohit", "ORD-1042", "shipped"], buttons: [], variables: { header: 0, body: 3 }, created_at: "2026-09-01T08:00:00.000Z", updated_at: null, meta_synced_at: "2026-09-01T08:05:00.000Z" }, body_text: "Hi {{1}}, your order {{2}} is now {{3}}.", body: "Hi {{1}}, your order {{2}} is now {{3}}.", language: "en_US", category: "UTILITY", status: "APPROVED" } },
      r400NoBody,
      r401,
      { code: 404, title: "Not Found", when: "No template with this name in your account.", body: { detail: "Template \"order_updatee\" not found" } },
      r500,
    ],
  },
  {
    id: "create-template",
    group: "Templates",
    name: "Create template",
    method: "POST",
    path: "/templates",
    auth: "live",
    summary: "Creates a template and submits it to Meta for review (status becomes PENDING, then APPROVED or REJECTED). Pass draft: true to save it without submitting.",
    params: [
      { name: "name", type: "string", required: true, desc: "Lowercase letters, numbers and underscores, e.g. order_update." },
      { name: "components", type: "array", required: true, desc: "HEADER / BODY / FOOTER / BUTTONS in Meta's format. Include example values for variables." },
      { name: "language", type: "string", required: false, desc: "Default en_US." },
      { name: "category", type: "string", required: false, desc: "MARKETING, UTILITY (default) or AUTHENTICATION." },
      { name: "draft", type: "boolean", required: false, desc: "true = save only, don't submit to Meta." },
    ],
    request: {
      name: "order_update",
      language: "en_US",
      category: "UTILITY",
      components: [
        { type: "HEADER", format: "TEXT", text: "Order update" },
        { type: "BODY", text: "Hi {{1}}, your order {{2}} is now {{3}}.", example: { body_text: [["Rohit", "ORD-1042", "shipped"]] } },
        { type: "FOOTER", text: "GrabNChat" },
      ],
    },
    responses: [
      { code: 200, title: "OK", when: "Template saved and submitted to Meta.", body: { data: { id: "7b3e…", name: "order_update", language: "en_US", category: "UTILITY", status: "PENDING", meta_template_id: "1275849302918374", created_at: "2026-09-29T10:15:42.118Z" } } },
      r400WaNotConnected,
      r401,
      r404Route("/api/v1/template"),
      { code: 422, title: "Unprocessable Entity", when: "Missing name/components, invalid category, or Meta rejected the submission (the saved draft is returned in data).", body: { detail: "Invalid category — must be MARKETING / UTILITY / AUTHENTICATION" } },
      r500,
    ],
  },

  // --------------------------------------------------------- WhatsApp Flows
  {
    id: "list-flows",
    group: "WhatsApp Flows",
    name: "List flows",
    method: "GET",
    path: "/whatsapp-flows",
    auth: "live",
    summary: "Lists the WhatsApp Flows (in-chat forms) in your account, most recently updated first.",
    params: [{ name: "limit", in: "query", type: "number", required: false, desc: "Default 100, max 500." }],
    query: "?limit=100",
    request: null,
    responses: [
      { code: 200, title: "OK", when: "List returned.", body: { data: [{ flow_id: "1234567890", name: "Lead capture", status: "PUBLISHED", categories: ["LEAD_GENERATION"], updated_at: "2026-09-20T12:00:00.000Z" }], count: 1 } },
      r400NoBody,
      r401,
      r404Route("/api/v1/whatsapp-flow"),
      r500,
    ],
  },
  {
    id: "create-flow",
    group: "WhatsApp Flows",
    name: "Create flow",
    method: "POST",
    path: "/whatsapp-flows",
    auth: "live",
    summary: "Creates a new DRAFT flow on Meta. Design and publish its screens from WhatsApp Flows in the portal.",
    params: [
      { name: "name", type: "string", required: true, desc: "Flow name." },
      { name: "categories", type: "string[]", required: true, desc: "e.g. LEAD_GENERATION, SIGN_UP, APPOINTMENT_BOOKING, CONTACT_US, CUSTOMER_SUPPORT, SURVEY, OTHER." },
      { name: "endpoint_uri", type: "string", required: false, desc: "Your data-exchange endpoint (for dynamic flows)." },
      { name: "clone_flow_id", type: "string", required: false, desc: "Copy an existing flow." },
    ],
    request: { name: "Lead capture", categories: ["LEAD_GENERATION"] },
    responses: [
      { code: 200, title: "OK", when: "Flow created on Meta.", body: { data: { flow_id: "1234567890", name: "Lead capture", status: "DRAFT", categories: ["LEAD_GENERATION"] } } },
      { code: 400, title: "Bad Request", when: "No WhatsApp Business Account is linked, or Meta rejected the request (Meta's error is returned in meta).", body: { detail: "WABA not linked" } },
      r401,
      r404Route("/api/v1/whatsapp-flow"),
      { code: 422, title: "Unprocessable Entity", when: "name/categories missing, or none of the categories are valid.", body: { detail: "At least one valid category required" } },
      r500,
    ],
  },
  {
    id: "send-flow",
    group: "WhatsApp Flows",
    name: "Send flow to a contact",
    method: "POST",
    path: "/whatsapp-flows/send",
    auth: "live",
    summary: "Sends a published flow as an interactive message with a call-to-action button. The returned flow_token is echoed back when the customer submits the form.",
    params: [
      { name: "to", type: "string", required: true, desc: "Recipient phone number." },
      { name: "flow_id", type: "string", required: true, desc: "Flow to open." },
      { name: "flow_cta", type: "string", required: true, desc: "Button label, e.g. \"Book now\"." },
      { name: "body", type: "string", required: false, desc: "Message text above the button." },
      { name: "header_text", type: "string", required: false, desc: "Optional header." },
      { name: "footer_text", type: "string", required: false, desc: "Optional footer." },
      { name: "flow_token", type: "string", required: false, desc: "Your own token; generated if omitted." },
      { name: "flow_action", type: "string", required: false, desc: "navigate (default) or data_exchange." },
      { name: "mode", type: "string", required: false, desc: "published (default) or draft for testing." },
    ],
    request: { to: "+919999999999", flow_id: "1234567890", flow_cta: "Book now", body: "Pick a time that suits you.", header_text: "Book a demo" },
    responses: [
      { code: 200, title: "OK", when: "Flow message sent.", body: { data: { wamid: "wamid.HBgMOTE5OTk5OTk5OTk5FQIAERgSRjU3QkE2QzM0RTVCMDg3AA==", flow_token: "9f86d081884c7d659a2feaa0", flow_id: "1234567890", to: "+919999999999" } } },
      { code: 400, title: "Bad Request", when: "WhatsApp is not connected, or Meta rejected the message (Meta's status and error are passed through).", body: { detail: "Meta send failed", meta: { error: { message: "(#131009) Parameter value is not valid", type: "OAuthException", code: 131009 } } } },
      r401,
      r404Route("/api/v1/whatsapp-flows/sendd"),
      r422Missing("flow_cta"),
      r500,
    ],
  },

  // --------------------------------------------------------------- Contacts
  {
    id: "upsert-contact",
    group: "Contacts",
    name: "Create or update a contact",
    method: "POST",
    path: "/contacts",
    auth: "live",
    summary: "Creates a contact, or updates the existing one with the same phone number. Tags are merged (never replaced) and custom_attributes are merged key-by-key; name and email are only filled in if currently blank — so several integrations can safely write to the same contact.",
    params: [
      { name: "phone", type: "string", required: true, desc: "Phone number in international format (used to find duplicates)." },
      { name: "name", type: "string", required: false, desc: "Contact name." },
      { name: "email", type: "string", required: false, desc: "Email address." },
      { name: "tags", type: "string[]", required: false, desc: "Tags to add." },
      { name: "custom_attributes", type: "object", required: false, desc: "Key/value fields to store on the contact." },
    ],
    request: { phone: "+919999999999", name: "Rohit Sharma", email: "rohit@example.com", tags: ["shopify-customer"], custom_attributes: { city: "Delhi", total_spent: "5400" } },
    responses: [
      { code: 200, title: "OK", when: "created is true for a new contact, false when an existing one was updated.", body: { created: true, contact: { id: "1a2b3c4d-5e6f-4a7b-8c9d-0e1f2a3b4c5d", company_id: "c0a8012e-5b6f-4c3d-9e21-7f8a9b0c1d2e", name: "Rohit Sharma", phone: "+919999999999", phone_key: "919999999999", email: "rohit@example.com", tags: ["shopify-customer"], notes: null, custom_attributes: { city: "Delhi", total_spent: "5400" }, created_at: "2026-09-29T10:15:42.118Z" } } },
      r400Json,
      r401,
      r404Route("/api/v1/contact"),
      { code: 422, title: "Unprocessable Entity", when: "phone is missing or not a valid phone number.", body: { detail: "Invalid phone number" } },
      r500,
    ],
  },

  // ------------------------------------------------------------------ Leads
  {
    id: "capture-lead",
    group: "Leads",
    name: "Capture a lead from your website",
    method: "POST",
    path: "/leads/capture",
    auth: "pub",
    summary: "Sends website form submissions straight into Lead Management. Uses a public gnc_pub_ key that can only create leads, so it is safe to put in browser JavaScript or a plain HTML form. CORS is open to any origin.",
    params: [
      { name: "name", type: "string", required: true, desc: "Lead name." },
      { name: "phone", type: "string", required: false, desc: "Phone. Either phone or email is required." },
      { name: "email", type: "string", required: false, desc: "Email. Either phone or email is required." },
      { name: "message", type: "string", required: false, desc: "Message / notes from the form." },
      { name: "source", type: "string", required: false, desc: "Lead source. Default website." },
      { name: "tags", type: "string[]", required: false, desc: "Tags for the lead." },
      { name: "page_url, referrer", type: "string", required: false, desc: "Page the form was submitted from." },
      { name: "utm_source, utm_medium, utm_campaign", type: "string", required: false, desc: "Campaign tracking." },
      { name: "redirect", type: "string", required: false, desc: "For plain HTML forms: redirect here (303) after saving instead of returning JSON." },
      { name: "api_key", type: "string", required: false, desc: "Alternative to the X-API-Key header (useful in HTML forms)." },
    ],
    request: { name: "Rohit Sharma", phone: "+919999999999", email: "rohit@example.com", message: "Interested in the Growth plan", page_url: "https://yoursite.com/contact", utm_source: "google", utm_medium: "cpc", utm_campaign: "diwali-sale" },
    notes: ["Rate limit: 60 submissions per 10 minutes per key.", "Include a hidden, empty _gotcha field in your form — bots that fill it are silently ignored."],
    responses: [
      { code: 201, title: "Created", when: "Lead saved.", body: { ok: true, lead: { id: "5e6f7a8b-9c0d-4e1f-a2b3-c4d5e6f7a8b9", name: "Rohit Sharma", stage: "new" } } },
      r400Json,
      { code: 401, title: "Unauthorized", when: "The gnc_pub_ key is missing, invalid or revoked.", body: { detail: "Invalid or revoked API key" } },
      r404Route("/api/v1/lead/capture"),
      { code: 422, title: "Unprocessable Entity", when: "name is missing, neither phone nor email was sent, or email is invalid.", body: { detail: "phone or email is required" } },
      { code: 429, title: "Too Many Requests", when: "More than 60 submissions in 10 minutes for this key.", body: { detail: "Too many submissions — try again later" } },
      r500,
    ],
  },

  // -------------------------------------------------------------- Analytics
  {
    id: "analytics",
    group: "Analytics",
    name: "Messaging analytics",
    method: "GET",
    path: "/analytics",
    auth: "live",
    summary: "Totals and a day-by-day breakdown of inbound and outbound messages, delivery statuses, top templates and flow submissions for the last N days.",
    params: [{ name: "days", in: "query", type: "number", required: false, desc: "Period length. Default 30, max 365." }],
    query: "?days=30",
    request: null,
    responses: [
      { code: 200, title: "OK", when: "Analytics returned.", body: { period_days: 30, since: "2026-08-30T10:15:42.118Z", totals: { messages_out: 421, messages_in: 318, messages_total: 739, conversations: 96, contacts: 87, flow_submissions: 12 }, by_status: [{ status: "read", count: 240 }, { status: "delivered", count: 150 }, { status: "failed", count: 31 }], by_type: [{ direction: "out", type: "template", count: 300 }], by_day: [{ day: "2026-09-28", in: 12, out: 18, total: 30 }], top_templates: [{ name: "order_update", count: 110 }] } },
      r400NoBody,
      r401,
      r404Route("/api/v1/analytic"),
      r500,
    ],
  },

  // --------------------------------------------------------------- Realtime
  {
    id: "socket-info",
    group: "Realtime",
    name: "Get realtime (Socket.IO) connection info",
    method: "GET",
    path: "/socket-info",
    auth: "live",
    summary: "Returns everything a mobile or server app needs to receive live events: Socket.IO URL and path, how to authenticate with your API key, and the events the server emits.",
    request: null,
    responses: [
      { code: 200, title: "OK", when: "Connection info returned.", body: { socket_url: API_HOST, path: "/api/socket.io", transports: ["websocket", "polling"], rooms: { auto_joined: ["company:<company_id> (joined automatically after auth)"] }, events_from_server: [{ name: "whatsapp_message", when: "A new inbound message arrives OR an outbound message is sent." }, { name: "company_new_message", when: "Lightweight ping for unread badge refresh." }, { name: "latest_status", when: "Delivery status update (sent / delivered / read / failed)." }, { name: "ready", when: "Once after successful authentication." }] } },
      r400NoBody,
      r401,
      r404Route("/api/v1/socketinfo"),
      r500,
    ],
  },
  {
    id: "catalog",
    group: "Realtime",
    name: "Get machine-readable API catalog",
    method: "GET",
    path: "/catalog",
    auth: "live",
    summary: "Returns the list of public endpoints with request and response examples as JSON — handy for generating clients.",
    request: null,
    responses: [
      { code: 200, title: "OK", when: "Catalog returned.", body: { base_url: "/api/v1", auth: { type: "api_key", header: "X-API-Key" }, endpoints: [{ group: "Health", name: "Ping", method: "GET", path: "/api/v1/ping" }] } },
      r400NoBody,
      r401,
      r404Route("/api/v1/catalogue"),
      r500,
    ],
  },
];

export const STATUS_CODES = [
  { code: 200, name: "OK", text: "The request succeeded." },
  { code: 201, name: "Created", text: "A new resource was created (lead capture)." },
  { code: 400, name: "Bad Request", text: "Malformed JSON, WhatsApp not connected, or a limit was exceeded." },
  { code: 401, name: "Unauthorized", text: "Missing, invalid or revoked API key." },
  { code: 402, name: "Payment Required", text: "Message wallet balance too low to send." },
  { code: 403, name: "Forbidden", text: "Company account is inactive." },
  { code: 404, name: "Not Found", text: "Wrong URL, or the template / conversation / company doesn't exist." },
  { code: 422, name: "Unprocessable Entity", text: "Validation failed — a required field is missing or invalid." },
  { code: 429, name: "Too Many Requests", text: "Rate limit reached. Wait and retry." },
  { code: 500, name: "Internal Server Error", text: "Unexpected server error. Retry with back-off." },
];

export const SOCKET_EVENTS = [
  { name: "whatsapp_message", text: "New inbound message, or an outbound message you sent." },
  { name: "latest_status", text: "Delivery status change: sent, delivered, read, failed." },
  { name: "company_new_message", text: "Lightweight ping to refresh unread counts." },
  { name: "ready", text: "Emitted once after the socket authenticates." },
];
